import { API_BASE_URL } from "./apiConfig";
import { apiFetch, getAuthHeaders, handleApiResponse } from "./apiClient";

const handleResponse = handleApiResponse;
const authFetch = apiFetch;

// === App 1-3: Conversation APIs ===
export const sendConversationMessage = async (payload) => {
  const response = await authFetch(
    `${API_BASE_URL}/conversation/message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    }
  );
  return handleResponse(response);
};

export const conversationMakerFinalizeOneshot = async (payload) => {
  const response = await authFetch(
    `${API_BASE_URL}/generator/conversation-maker/finalize-oneshot`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    }
  );
  return handleResponse(response);
};

// === App 8: Workspace (프로젝트 관리 - Supabase 연동) ===

/**
 * [GET] 모든 프로젝트 조회 (Supabase에서)
 */
export const getProjects = async () => {
  const response = await authFetch(`${API_BASE_URL}/workspace/projects`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * [POST] 새 프로젝트 생성 (Supabase에 저장)
 * @param {object} projectData - { name, domain, tags, productUrl }
 */
export const createProject = async (projectData) => {
  const response = await authFetch(`${API_BASE_URL}/workspace/projects`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(projectData),
  });
  return handleResponse(response);
};

/**
 * [DELETE] 프로젝트 삭제
 */
export const deleteProject = async (projectId) => {
  const response = await authFetch(
    `${API_BASE_URL}/workspace/projects/${projectId}`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    }
  );
  return handleResponse(response);
};

/**
 * [PUT] 프로젝트 정보 수정
 * @param {number} projectId - 프로젝트 ID
 * @param {object} projectData - { name, domain, tags }
 */
export const updateProject = async (projectId, projectData) => {
  const response = await authFetch(
    `${API_BASE_URL}/workspace/projects/${projectId}`,
    {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(projectData),
    }
  );
  return handleResponse(response);
};

/**
 * [POST] 프로젝트명 자동 생성
 * @param {string} studyName - 연구명
 * @param {string} problemDefinition - 문제 정의
 */
export const generateProjectName = async (studyName, problemDefinition) => {
  const response = await authFetch(`${API_BASE_URL}/workspace/generate-project-name`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ studyName, problemDefinition }),
  });
  return handleResponse(response);
};

/**
 * [POST] 연구명 자동 생성
 * @param {string} problemDefinition - 문제 정의
 */
export const generateStudyName = async (problemDefinition) => {
  const response = await authFetch(`${API_BASE_URL}/workspace/generate-study-name`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ problemDefinition }),
  });
  return handleResponse(response);
};

/**
 * [GET] 프로젝트의 연구 목록 조회
 * @param {number} projectId - 프로젝트 ID
 */
export const getProjectStudies = async (projectId) => {
  const response = await authFetch(
    `${API_BASE_URL}/projects/${projectId}/studies`,
    { method: "GET" }
  );
  return handleResponse(response);
};

/**
 * [GET] 프로젝트와 스터디를 한 번에 조회 (통합 엔드포인트)
 * - N+1 쿼리 문제 해결
 * - 프로젝트와 각 프로젝트의 스터디를 한 번에 반환
 */
export const getProjectsWithStudies = async () => {
  const response = await authFetch(
    `${API_BASE_URL}/workspace/projects-with-studies`,
    { method: "GET" }
  );
  return handleResponse(response);
};

/**
 * [GET] 선택 모달에서 필요한 완료된 계획서/가이드라인만 조회
 * @param {string[]} artifactTypes - plan, guideline 중 필요한 유형
 */
export const getWorkspaceArtifactOptions = async ({ artifactTypes = [] } = {}) => {
  const params = new URLSearchParams({ artifact_types: artifactTypes.join(",") });
  const response = await authFetch(
    `${API_BASE_URL}/workspace/artifact-options?${params.toString()}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

/** Fetch the body of one selected workspace artifact. */
export const getWorkspaceArtifactContent = async (artifactId) => {
  const response = await authFetch(
    `${API_BASE_URL}/workspace/artifacts/${artifactId}/content`,
    { method: "GET" }
  );
  return handleResponse(response);
};

/**
 * [GET] 연구의 아티팩트 목록 조회
 * @param {number} studyId - 연구 ID
 */
export const getStudyArtifacts = async (studyId) => {
  const response = await authFetch(`${API_BASE_URL}/studies/${studyId}/artifacts`, {
    method: "GET",
  });
  return handleResponse(response);
};

/** 개발 전용: 산출물 평가 (plan/survey/guideline/report 범용 테스트기) */
export const devEvaluate = async (body) => {
  const response = await authFetch(`${API_BASE_URL}/dev/evaluate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  return response.ok ? response.json() : response.json().then((d) => { throw new Error(d.error || "평가 실패"); });
};

export const screenerAnalyzePlanV2 = async (planText, studyId) => {
  const response = await authFetch(`${API_BASE_URL}/screener/analyze-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      plan_text: planText,
      study_id: studyId 
    }),
  });
  return handleResponse(response);
};


export const screenerUploadCsvV2 = async (csvContent) => {
  const response = await authFetch(`${API_BASE_URL}/screener/upload-csv`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ csv_content: csvContent }),
  });
  return handleResponse(response);
};


// === Screener: 일정 최적화 API ===
export const optimizeSchedule = async (
  participantsData,
  scheduleColumns,
  nameColumn,
  targetGroups = [],
  studyId = null,
  hasNameColumn = false
) => {
  const scheduleColumnList = Array.isArray(scheduleColumns) ? scheduleColumns : [];

  const resolveParticipantName = (participant) => {
    if (!participant || typeof participant !== 'object') return '';

    const nameFromColumn = nameColumn && participant[nameColumn]
      ? String(participant[nameColumn]).trim()
      : '';

    if (nameFromColumn) return nameFromColumn;

    if (participant._display_name) {
      return String(participant._display_name).trim();
    }

    if (participant.participant_name) {
      return String(participant.participant_name).trim();
    }

    if (participant.name) {
      return String(participant.name).trim();
    }

    if (participant.id) {
      return String(participant.id).trim();
    }

    if (participant.participant_id) {
      return String(participant.participant_id).trim();
    }

    return '';
  };

  const preparedParticipants = participantsData.map(participant => {
    if (!participant || typeof participant !== 'object') return participant;

    const prepared = { ...participant };
    const fallbackName = resolveParticipantName(participant);

    // name_column이 없거나 비어있으면 fallback 사용
    if (!hasNameColumn && nameColumn && fallbackName) {
      const currentValue = prepared[nameColumn];
      if (!currentValue || String(currentValue).trim() === '') {
        prepared[nameColumn] = fallbackName;
      }
    }

    if (scheduleColumnList.length > 0) {
      const scheduleValues = participant._schedule_values;
      if (scheduleValues && typeof scheduleValues === 'object') {
        scheduleColumnList.forEach(col => {
          if (!prepared[col] && scheduleValues[col]) {
            prepared[col] = scheduleValues[col];
          }
        });
      }
    }

    return prepared;
  });

  const requiredParticipants = preparedParticipants
    .map(resolveParticipantName)
    .filter(name => !!name);

  const requestBody = {
    participants_data: preparedParticipants,
    schedule_columns: scheduleColumnList,
    name_column: nameColumn,
    target_groups: targetGroups,
    required_participants: requiredParticipants,
    has_name_column: hasNameColumn,
  };
  
  // studyId가 있으면 추가
  if (studyId) {
    requestBody.study_id = studyId;
  }
  
  const response = await authFetch(`${API_BASE_URL}/screener/optimize-schedule`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });
  return handleResponse(response);
};

/**
 * 스마트 선정 및 일정 생성: 선택된 참여자 포함하여 부족한 인원만 자동 선정 후 일정 생성
 */
export const finalizeParticipants = async (studyId, participantsData, selectedParticipantIds, targetGroups, csvInfo, planJson) => {
  const response = await authFetch(`${API_BASE_URL}/screener/finalize-participants`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      study_id: studyId,
      participants_data: participantsData,
      selected_participants: selectedParticipantIds,
      target_groups: targetGroups,
      csv_info: csvInfo,
      plan_json: planJson,
    }),
  });
  return handleResponse(response);
};


export const saveSchedule = async (studyId, optimizedSchedule, participantsData, scheduleColumns, nameColumn, targetGroups, validationData) => {
  const response = await authFetch(`${API_BASE_URL}/screener/save-schedule`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      study_id: studyId,
      optimized_schedule: optimizedSchedule,
      participants_data: participantsData,
      schedule_columns: scheduleColumns,
      name_column: nameColumn,
      target_groups: targetGroups,
      validation_data: validationData
    }),
  });
  return handleResponse(response);
};

// === Admin APIs ===

// --- Super Admin: 기업 계정 관리 ---

/**
 * [GET] 기업 계정 목록 조회 (전체 계정 포함)
 * @param {object} params - { search, account_type, company_role, page, per_page }
 */
export const getEnterpriseAccounts = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.search) query.append('search', params.search);
  if (params.account_type) query.append('account_type', params.account_type);
  if (params.company_role) query.append('company_role', params.company_role);
  if (params.page) query.append('page', params.page);
  if (params.per_page) query.append('per_page', params.per_page);
  const qs = query.toString();
  const url = `${API_BASE_URL}/admin/enterprise/accounts${qs ? `?${qs}` : ''}`;
  const response = await authFetch(url, { method: "GET" });
  return handleResponse(response);
};

export const getPersonaPresets = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/persona-presets`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getPersonaSchemaTemplates = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/persona-schema/templates`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getPersonaDomainTemplates = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.include_archived) query.append("include_archived", "true");
  const qs = query.toString();
  const response = await authFetch(
    `${API_BASE_URL}/admin/persona-domain-templates${qs ? `?${qs}` : ""}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

export const getPersonaDomainTemplate = async (templateId) => {
  const response = await authFetch(
    `${API_BASE_URL}/admin/persona-domain-templates/${templateId}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

export const createPersonaDomainTemplate = async (data) => {
  const response = await authFetch(`${API_BASE_URL}/admin/persona-domain-templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data || {}),
  });
  return handleResponse(response);
};

export const updatePersonaDomainTemplate = async (templateId, data) => {
  const response = await authFetch(
    `${API_BASE_URL}/admin/persona-domain-templates/${templateId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data || {}),
    }
  );
  return handleResponse(response);
};

export const archivePersonaDomainTemplate = async (templateId) => {
  const response = await authFetch(
    `${API_BASE_URL}/admin/persona-domain-templates/${templateId}`,
    { method: "DELETE" }
  );
  return handleResponse(response);
};

export const getCompanyPersonaSchema = async (companyId, params = {}) => {
  const query = new URLSearchParams();
  if (params.preset_id || params.presetId) {
    query.append("preset_id", params.preset_id || params.presetId);
  }
  const qs = query.toString();
  const response = await authFetch(
    `${API_BASE_URL}/admin/companies/${companyId}/persona-schema${qs ? `?${qs}` : ""}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

export const saveCompanyPersonaSchema = async (companyId, data) => {
  const response = await authFetch(
    `${API_BASE_URL}/admin/companies/${companyId}/persona-schema`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data || {}),
    }
  );
  return handleResponse(response);
};

/** @deprecated use saveCompanyPersonaSchema */
export const previewCompanyPersonaSchema = saveCompanyPersonaSchema;

/**
 * [POST] 기업 계정 생성 (오너 계정 + 회사 연결)
 * @param {object} data - { email, name, company_name, department, personaPresetId }
 */
export const createEnterpriseAccount = async (data) => {
  const response = await authFetch(`${API_BASE_URL}/admin/enterprise/accounts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * [PUT] 기업 계정 정보 수정
 * @param {number} accountId
 * @param {object} data - { name, company_name, department }
 */
export const updateEnterpriseAccount = async (accountId, data) => {
  const response = await authFetch(`${API_BASE_URL}/admin/enterprise/accounts/${accountId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * [POST] 기업 계정 비밀번호 초기화
 * new_password를 보내지 않으면 서버에서 기본 비밀번호(0000)로 설정
 * @param {number} accountId
 */
export const resetEnterprisePassword = async (accountId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/enterprise/accounts/${accountId}/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  return handleResponse(response);
};

/**
 * [DELETE] 관리자 사용자 삭제
 * @param {number} userId
 */
export const deleteAdminUser = async (userId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: "DELETE",
  });
  return handleResponse(response);
};

// --- Super Admin: 회사 관리 ---

export const getCompanies = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.search) query.append('search', params.search);
  if (params.status) query.append('status', params.status);
  if (params.page) query.append('page', params.page);
  if (params.per_page) query.append('per_page', params.per_page);
  const qs = query.toString();
  const url = `${API_BASE_URL}/admin/companies${qs ? `?${qs}` : ''}`;
  const response = await authFetch(url, { method: "GET" });
  return handleResponse(response);
};

export const getCompanyDetail = async (companyId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/companies/${companyId}`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const updateCompany = async (companyId, data) => {
  const response = await authFetch(`${API_BASE_URL}/admin/companies/${companyId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const deleteCompany = async (companyId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/companies/${companyId}`, {
    method: "DELETE",
  });
  return handleResponse(response);
};

// --- Super Admin: LLM 사용량/토큰 잔액 ---

/**
 * [GET] 회사 전체 LLM 사용량 조회
 * @param {number} companyId - companies 테이블의 ID
 * @param {object} params - { period, start_date, end_date }
 */
export const getCompanyUsage = async (companyId, params = {}) => {
  const query = new URLSearchParams();
  if (params.period) query.append('period', params.period);
  if (params.start_date) query.append('start_date', params.start_date);
  if (params.end_date) query.append('end_date', params.end_date);
  const qs = query.toString();
  const url = `${API_BASE_URL}/admin/companies/${companyId}/llm-usage${qs ? `?${qs}` : ''}`;
  const response = await authFetch(url, { method: "GET" });
  return handleResponse(response);
};

export const getUserLlmUsage = async (userId, params = {}) => {
  const query = new URLSearchParams();
  if (params.period) query.append('period', params.period);
  if (params.start_date) query.append('start_date', params.start_date);
  if (params.end_date) query.append('end_date', params.end_date);
  const qs = query.toString();
  const url = `${API_BASE_URL}/admin/users/${userId}/llm-usage${qs ? `?${qs}` : ''}`;
  const response = await authFetch(url, { method: "GET" });
  return handleResponse(response);
};

export const getTeamLlmUsage = async (teamId, params = {}) => {
  const query = new URLSearchParams();
  if (params.period) query.append('period', params.period);
  if (params.start_date) query.append('start_date', params.start_date);
  if (params.end_date) query.append('end_date', params.end_date);
  const qs = query.toString();
  const url = `${API_BASE_URL}/admin/teams/${teamId}/llm-usage${qs ? `?${qs}` : ''}`;
  const response = await authFetch(url, { method: "GET" });
  return handleResponse(response);
};

export const getCompanyTokenBalance = async (companyId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/companies/${companyId}/token-balance`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const createCompanyTokenTopup = async (companyId, data) => {
  const response = await authFetch(`${API_BASE_URL}/admin/companies/${companyId}/token-topups`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data || {}),
  });
  return handleResponse(response);
};

export const createAccountTokenTopup = async (accountId, data) => {
  const response = await authFetch(`${API_BASE_URL}/admin/accounts/${accountId}/token-topups`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data || {}),
  });
  return handleResponse(response);
};

export const resetCompanyTokenMetering = async (companyId, data = {}) => {
  const response = await authFetch(`${API_BASE_URL}/admin/companies/${companyId}/token-reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data || {}),
  });
  return handleResponse(response);
};

export const getLlmModelPrices = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.active_only !== undefined) query.append('active_only', params.active_only);
  if (params.provider) query.append('provider', params.provider);
  const qs = query.toString();
  const response = await authFetch(`${API_BASE_URL}/admin/llm-model-prices${qs ? `?${qs}` : ''}`, {
    method: "GET",
  });
  return handleResponse(response);
};

// --- Super Admin: Interview Corpus workspace ---

export const getCorpusOverview = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/corpus/overview`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getCorpusSources = async (companyId, params = {}) => {
  const query = new URLSearchParams();
  if (params.status) query.append("status", params.status);
  if (params.preset_id || params.presetId) {
    query.append("preset_id", params.preset_id || params.presetId);
  }
  if (params.template_id || params.templateId) {
    query.append("template_id", params.template_id || params.templateId);
  }
  if (params.corpus_scope || params.corpusScope) {
    query.append("corpus_scope", params.corpus_scope || params.corpusScope);
  }
  const qs = query.toString();
  const response = await authFetch(
    `${API_BASE_URL}/admin/corpus/companies/${companyId}/sources${qs ? `?${qs}` : ""}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

export const getCorpusDomainSources = async (templateId, params = {}) => {
  const query = new URLSearchParams();
  if (params.status) query.append("status", params.status);
  const qs = query.toString();
  const encoded = encodeURIComponent(templateId);
  const response = await authFetch(
    `${API_BASE_URL}/admin/corpus/domains/${encoded}/sources${qs ? `?${qs}` : ""}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

export const getChromaOverview = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/corpus/chroma/overview`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getChromaVectors = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.data_type || params.dataType) {
    query.append("data_type", params.data_type || params.dataType);
  }
  if (params.template_id || params.templateId) {
    query.append("template_id", params.template_id || params.templateId);
  }
  if (params.corpus_scope || params.corpusScope) {
    query.append("corpus_scope", params.corpus_scope || params.corpusScope);
  }
  if (params.company_id || params.companyId) {
    query.append("company_id", String(params.company_id || params.companyId));
  }
  if (params.query || params.q) {
    query.append("query", params.query || params.q);
  }
  if (params.limit != null) query.append("limit", String(params.limit));
  if (params.offset != null) query.append("offset", String(params.offset));
  const qs = query.toString();
  const response = await authFetch(
    `${API_BASE_URL}/admin/corpus/chroma/vectors${qs ? `?${qs}` : ""}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

export const uploadCorpusSources = async (companyId, { files, presetId, templateId, corpusScope }) => {
  const formData = new FormData();
  (files || []).forEach((file) => formData.append("files", file));
  const domainId = templateId || presetId;
  if (domainId) {
    formData.append("template_id", domainId);
    formData.append("preset_id", domainId);
  }
  if (corpusScope) formData.append("corpus_scope", corpusScope);
  const response = await authFetch(
    `${API_BASE_URL}/admin/corpus/companies/${companyId}/sources`,
    { method: "POST", body: formData }
  );
  return handleResponse(response);
};

export const processCorpusSources = async (companyId, { sourceIds } = {}) => {
  const response = await authFetch(
    `${API_BASE_URL}/admin/corpus/companies/${companyId}/process`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source_ids: sourceIds || undefined }),
    }
  );
  return handleResponse(response);
};

// --- 기존 Admin APIs ---

export const getAllUsersAdmin = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/users`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getUserEnterpriseInfo = async (userId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/users/${userId}/enterprise`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const initEnterpriseTeamForUser = async (userId, companyName, department = "", personaPresetId = "telecom") => {
  const response = await authFetch(`${API_BASE_URL}/admin/users/${userId}/enterprise/init-team`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      company_name: companyName,
      department,
      personaPresetId: personaPresetId,
    }),
  });
  return handleResponse(response);
};

export const updateUserTier = async (userId, tier) => {
  const response = await authFetch(`${API_BASE_URL}/admin/users/${userId}/tier`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tier }),
  });
  return handleResponse(response);
};

export const getAdminStats = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/stats`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getAnalyticsOverview = async (params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))
  ).toString();
  const response = await authFetch(`${API_BASE_URL}/admin/analytics/overview${qs ? `?${qs}` : ""}`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getAnalyticsFunnels = async () => {
  const response = await authFetch(`${API_BASE_URL}/admin/analytics/funnels`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getAnalyticsFunnel = async (funnelId, params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))
  ).toString();
  const response = await authFetch(
    `${API_BASE_URL}/admin/analytics/funnels/${funnelId}${qs ? `?${qs}` : ""}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

export const getAnalyticsEvents = async (params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))
  ).toString();
  const response = await authFetch(`${API_BASE_URL}/admin/analytics/events${qs ? `?${qs}` : ""}`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getAnalyticsCompany = async (companyId, params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))
  ).toString();
  const response = await authFetch(
    `${API_BASE_URL}/admin/analytics/companies/${companyId}${qs ? `?${qs}` : ""}`,
    { method: "GET" }
  );
  return handleResponse(response);
};

export const getUserProjects = async (userId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/users/${userId}/projects`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const getUserStudies = async (userId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/users/${userId}/studies`, {
    method: "GET",
  });
  return handleResponse(response);
};

/**
 * [GET] Admin 전용 - Study 조회 (권한 검증 없이)
 * @param {number} studyId - 연구 ID
 */
export const getAdminStudy = async (studyId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/studies/${studyId}`, {
    method: "GET",
  });
  return handleResponse(response);
};

/**
 * [GET] Admin 전용 - Study의 Artifacts 조회 (권한 검증 없이)
 * @param {number} studyId - 연구 ID
 */
export const getAdminStudyArtifacts = async (studyId) => {
  const response = await authFetch(`${API_BASE_URL}/admin/studies/${studyId}/artifacts`, {
    method: "GET",
  });
  return handleResponse(response);
};

// === B2B Team APIs ===
export const getMyTeam = async () => {
  const response = await authFetch(`${API_BASE_URL}/b2b/team`, {
    method: "GET",
  });
  return handleResponse(response);
};

export const addTeamMember = async (email, role = "member", department = "", name = "") => {
  const response = await authFetch(`${API_BASE_URL}/b2b/team/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role, department, name }),
  });
  return handleResponse(response);
};

export const removeTeamMember = async (userId) => {
  const response = await authFetch(`${API_BASE_URL}/b2b/team/members/${userId}`, {
    method: "DELETE",
  });
  return handleResponse(response);
};

export const changeTeamMemberRole = async (userId, role = "owner") => {
  const response = await authFetch(`${API_BASE_URL}/b2b/team/members/${userId}/role`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  return handleResponse(response);
};

export const updateTeamMemberProfile = async (userId, data) => {
  const response = await authFetch(`${API_BASE_URL}/b2b/team/members/${userId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

export const resetTeamMemberPassword = async (userId) => {
  const response = await authFetch(`${API_BASE_URL}/b2b/team/members/${userId}/reset-password`, {
    method: "POST",
  });
  return handleResponse(response);
};

export const getB2BMembershipUsage = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.period) query.append('period', params.period);
  if (params.start_date) query.append('start_date', params.start_date);
  if (params.end_date) query.append('end_date', params.end_date);
  const qs = query.toString();
  const response = await authFetch(`${API_BASE_URL}/b2b/membership/usage${qs ? `?${qs}` : ''}`, {
    method: "GET",
  });
  return handleResponse(response);
};

// === 계정 삭제 API ===
export const deleteAccount = async () => {
  const response = await authFetch(`${API_BASE_URL}/auth/account`, {
    method: "DELETE",
  });
  return handleResponse(response);
};

// === 피드백 API ===
export const submitFeedback = async (category, vote, comment = '', studyId = null, studyName = '') => {
  const response = await authFetch(`${API_BASE_URL}/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      category: category,
      vote: vote, // true = 좋아요, false = 싫어요
      comment: comment,
      study_id: studyId,
      study_name: studyName
    }),
  });
  return handleResponse(response);
};

export const getFeedback = async (category = null) => {
  let url = `${API_BASE_URL}/admin/feedback`;
  if (category) {
    url += `?category=${encodeURIComponent(category)}`;
  }
  const response = await authFetch(url, {
    method: "GET",
  });
  return handleResponse(response);
};

// === Business Auth APIs ===

/**
 * [POST] 기업 로그인
 * @param {string} email
 * @param {string} password
 */
export const enterpriseLogin = async (email, password) => {
  const response = await authFetch(`${API_BASE_URL}/auth/business/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(response);
};

/**
 * [POST] 기업 비밀번호 변경
 * @param {string} currentPassword
 * @param {string} newPassword
 */
export const enterpriseChangePassword = async (currentPassword, newPassword) => {
  const response = await authFetch(`${API_BASE_URL}/auth/business/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  return handleResponse(response);
};

/**
 * [PUT] 기업회원 본인 프로필 수정
 * @param {object} data - { name, department }
 */
export const updateEnterpriseProfile = async (data) => {
  const response = await authFetch(`${API_BASE_URL}/auth/business/profile`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

// === Admin Enterprise APIs ===

/**
 * [POST] 기업 사용자 생성 (회사 멤버 생성)
 * @param {object} userData - { email, name, company_id, department, role }
 */
export const createEnterpriseUser = async (userData) => {
  const response = await authFetch(`${API_BASE_URL}/admin/enterprise/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(userData),
  });
  return handleResponse(response);
};

export const updateFeedbackComment = async (feedbackId, comment) => {
  const response = await authFetch(`${API_BASE_URL}/feedback/${feedbackId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      comment: comment
    }),
  });
  return handleResponse(response);
};

// === Session / Auth APIs ===

/**
 * [POST] 로그아웃 - 서버 측 쿠키 제거
 */
export const logout = async () => {
  const response = await authFetch(`${API_BASE_URL}/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  // 401이어도 로그아웃은 성공으로 처리 (쿠키가 이미 만료되었을 수 있음)
  if (response.status === 401) {
    return { success: true };
  }
  return handleResponse(response);
};

/**
 * [GET] 현재 세션/프로필 조회 - 앱 부팅 시 로그인 상태 확인용
 * handleResponse를 거치지 않음 - 401은 "비로그인" 정상 상태이므로 리다이렉트 불필요
 */
export const getProfile = async () => {
  const response = await authFetch(`${API_BASE_URL}/profile`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (response.status === 401 || response.status === 403) {
    return { success: false, status: response.status };
  }
  if (!response.ok) {
    return { success: false, status: response.status };
  }
  const data = await response.json().catch(() => null);
  if (!data) {
    return { success: false, status: response.status };
  }

  if (data.success === true && data.user) {
    return data;
  }
  if (data.user) {
    return { ...data, success: true, user: data.user };
  }
  if (data.profile) {
    return { ...data, success: true, user: data.profile };
  }
  if (data.id || data.email) {
    return { success: true, user: data };
  }

  console.warn("Unexpected profile response shape:", data);
  return { success: false, status: response.status };
};
